import { faAngleLeft } from "@fortawesome/free-solid-svg-icons/faAngleLeft";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons/faAngleRight";
import { faCalendarCheck } from "@fortawesome/free-solid-svg-icons/faCalendarCheck";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons/faRotateLeft";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons/faEllipsis";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faFlag } from "@fortawesome/free-solid-svg-icons/faFlag";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons/faPenToSquare";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons/faTrashCan";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { isBefore, isToday, isTomorrow } from "date-fns";
import { TFunction } from "i18next";
import isFunction from "lodash/isFunction";
import isNumber from "lodash/isNumber";
import styled from "styled-components";
import { Tooltip } from "../../../utils/local-antd";
import type { MenuProps } from "../../../utils/local-antd";

import { AssigneeBanner } from "../../../components/AssigneeBanner";
import { AtiraDropdown } from "../../../components/AtiraDropdown";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import i18n from "../../../i18n";

import { Permission } from "../../../model/permissions/Permission";

import { User } from "../../../model/user/User";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { standardDate } from "../../../utils/Date";
import {
  BaseTask,
  ContactTask,
  LeadTask,
  OrphanTask,
} from "../../../model/task/Task";
import { TaskKind } from "../../../model/task/shared/TasksKind.enum";
import { Entry } from "../../../model/entry/Entry";
import { KanbanCard } from "../../../model/kanban/KanbanCard";
import { TableActionableCellPreview } from "../../../components/table/TableActionableCellPreview";
import { TestIDs } from "../../../tests/types/testIds";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";

const ActionContainer = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
`;

const RelatedToButton = styled(Button)`
  background-color: transparent;
  padding: 0;
  margin: 0;
  color: ${({ theme }) => theme.textColor};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.s};
  width: 100%;

  p {
    max-width: 10rem;
  }
`;

type DeadlineCellStatus = "danger" | "warning" | "success";

const DeadlineCell = styled(Flex)<{ $status: DeadlineCellStatus }>`
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};
  width: 100%;
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.md};
  background-color: ${({ theme, $status }) => theme[$status]};
  color: ${({ theme }) => theme.white};

  p {
    color: inherit;
  }
`;

const DeadlineStatusText = styled(Text)`
  font-size: 0.75rem;
  line-height: 1;
  opacity: 0.85;
`;

const DeadlineDateText = styled(Text)`
  font-weight: 600;
  line-height: 1.15;
`;

const PriorityCell = styled(Flex)<{ $status: DeadlineCellStatus }>`
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  width: 100%;
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.md};
  background-color: ${({ theme, $status }) => theme[$status]};
  color: ${({ theme }) => theme.white};

  p,
  svg {
    color: inherit;
  }
`;

type TaskATVoidFunction = ATVoidFunction<BaseTask>;
type FieldFunctions = {
  deleteTask: TaskATVoidFunction;
  updateTask: TaskATVoidFunction;
  previewTask: TaskATVoidFunction;
  assign?: TaskATVoidFunction;
  toggleDone: TaskATVoidFunction;
  t: TFunction;
  previewRelatedTo: TaskATVoidFunction;
};

// Define a type that includes all keys of LeadTask plus the 'action', 'priority' and 'relatedTo' keys
type TaskKeys =
  | keyof LeadTask
  | "actions"
  | "priority"
  | "relatedTo"
  | "deadline";

const TableColumnKeys: TaskKeys[] = [
  "actions",
  "name",
  "assigneeIds",
  "done",
  "relatedTo",
  "reminder",
  "deadline",
  "description",
  "priority",
  "createdAt",
];

const getFieldWidth = (field: string) => {
  switch (true) {
    case ["assigneeIds", "done", "actions"].includes(field):
      return "1%";
    case field === "relatedTo":
      return 300;
    default:
      return 200;
  }
};

const getNameCellClass = (taskKind?: TaskKind) => {
  switch (taskKind) {
    case TaskKind.CONTACT:
      return "contact-task";
    case TaskKind.KANBAN:
      return "kanban-task";
    case TaskKind.LEAD:
      return "lead-task";
    case TaskKind.ORPHAN:
      return "orphan-task";
    default:
      return "";
  }
};

const getDeadlineCellStatus = (date?: Date | null): DeadlineCellStatus => {
  const today = new Date();
  if (date) {
    if (isToday(date) || isBefore(date, today)) {
      return "danger";
    } else if (isTomorrow(date)) {
      return "warning";
    } else {
      return "success";
    }
  }

  return "success";
};

const getDeadlineStatusLabel = (date: Date, t: TFunction) => {
  if (isToday(date)) {
    return t("common.today");
  }

  if (isTomorrow(date)) {
    return t("common.tomorrow");
  }

  if (isBefore(date, new Date())) {
    return t("common.out_due");
  }

  return t("common.deadline");
};

const TaskNamePreview = styled(TableActionableCellPreview)`
  .actionable-cell {
    background-color: transparent;

    p {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    p {
      color: ${({ theme }) => theme.textColor} !important;
    }
  }
`;

const getFieldInfo = (
  column: TaskKeys,
  functions: FieldFunctions,
  permissions: Permission["permissions"],
  user: User,
  members?: User[],
) => {
  const { t, deleteTask, updateTask, previewTask, assign, toggleDone } =
    functions;

  switch (column) {
    case "actions":
      return {
        title: t("common.actions"),
        key: "actions",
        render: (task: BaseTask) => {
          const items: MenuProps["items"] = [
            {
              key: "g1",
              type: "group",
              children: [
                {
                  label: (
                    <ActionContainer
                      dataTestId={TestIDs.tasks.table.menu.edit(task._id)}
                    >
                      <AtiraIcon icon={faPenToSquare} color="main" />
                      <Text color="main">{t("common.edit")}</Text>
                    </ActionContainer>
                  ),
                  valid:
                    permissions[`${task.kind}_TASK` as keyof typeof permissions]
                      ?.UPDATE && !Boolean(task?.done),
                  onClick: () => updateTask(task),
                },
                {
                  label: (
                    <ActionContainer
                      dataTestId={TestIDs.tasks.table.menu.preview(task._id)}
                    >
                      <AtiraIcon icon={faEye} color="main" />
                      <Text color="main">{t("common.preview")}</Text>
                    </ActionContainer>
                  ),
                  valid:
                    permissions[`${task.kind}_TASK` as keyof typeof permissions]
                      ?.READ,
                  onClick: () => previewTask(task),
                },
                {
                  label: (
                    <ActionContainer
                      dataTestId={TestIDs.tasks.table.menu.done.button(
                        task._id,
                      )}
                    >
                      <AtiraIcon
                        icon={task.done ? faRotateLeft : faCheck}
                        color="main"
                      />
                      <Text color="main">
                        {task.done ? t("common.mark-undone") : t("common.done")}
                      </Text>
                    </ActionContainer>
                  ),
                  valid:
                    permissions[`${task.kind}_TASK` as keyof typeof permissions]
                      ?.UPDATE,
                  onClick: () => toggleDone(task),
                },
                {
                  label: (
                    <ActionContainer
                      dataTestId={TestIDs.tasks.table.menu.delete.button(
                        task._id,
                      )}
                    >
                      <AtiraIcon icon={faTrashCan} color="main" />
                      <Text color="main">{t("common.delete")}</Text>
                    </ActionContainer>
                  ),
                  valid:
                    permissions[`${task.kind}_TASK` as keyof typeof permissions]
                      ?.DELETE,
                  onClick: () => deleteTask(task),
                },
                {
                  label: (
                    <ActionContainer
                      dataTestId={TestIDs.tasks.table.menu.assign.button(
                        task._id,
                      )}
                    >
                      <AtiraIcon icon={faPenToSquare} color="main" />
                      <Text color="main">{t("common.assign")}</Text>
                    </ActionContainer>
                  ),
                  valid: isFunction(assign) && !task.done,
                  onClick: () => assign?.(task),
                },
              ]
                .filter((item) => item.valid !== false)
                .map((child, index) => ({
                  ...child,
                  key: index,
                  style: { padding: 4 },
                })),
            },
          ];

          return {
            children: (
              <Flex justifyContent="center">
                <AtiraDropdown
                  items={items}
                  placement="bottomLeft"
                  dataTestId={TestIDs.tasks.table.actionsCell(task._id)}
                >
                  <Tooltip title={t("common.menu")}>
                    <Button
                      icon={faEllipsis}
                      backgroundColor="transparent"
                      margin="none"
                      color="black"
                    />
                  </Tooltip>
                </AtiraDropdown>
              </Flex>
            ),
          };
        },
        reorder: true,
        center: true,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
        data: { column, enabled: true },
        fixed: "left",
      };

    case "name":
      return {
        title: t("common.name"),
        key: "name",
        render: (task: BaseTask) => {
          return {
            props: { className: getNameCellClass(task.kind) },
            children: (
              <TaskNamePreview
                text={task?.name}
                onPreview={() => previewTask(task)}
              />
            ),
          };
        },
        sorter: (task: BaseTask, _task: BaseTask) =>
          task.name.localeCompare(_task.name, "en", { sensitivity: "base" }),
        sortable: true,
        reorder: true,
        data: { column, enabled: true },
        onCell: (task: BaseTask) => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
        width: 320,
        fixed: "left",
      };

    case "assigneeIds":
      return {
        title: t("common.assignees"),
        key: "assignee",
        render: (task: BaseTask) => ({
          children: (
            <AssigneeBanner
              assignees={members?.filter((m) =>
                task.assigneeIds?.includes(m._id),
              )}
            />
          ),
        }),
        data: { column, enabled: true },
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
        fixed: "left",
      };

    /** time is the document's created_at field */
    case "createdAt":
      return {
        title: t("common.createdAt"),
        key: "time",
        render: ({ createdAt }: BaseTask) => (
          <Text>{standardDate<string>(new Date(createdAt), user, true)}</Text>
        ),
        sorter: (a: BaseTask, b: BaseTask) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateA - dateB;
        },
        sortable: true,
        reorder: true,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
        data: { column, enabled: true },
      };

    case "reminder":
      return {
        title: t("common.remind_date"),
        key: "date",
        render: ({ reminder }: BaseTask) => {
          return {
            children: reminder ? (
              <Text>
                {standardDate<string>(new Date(reminder), user, true)}
              </Text>
            ) : null,
          };
        },
        sorter: (_: BaseTask, __: BaseTask) => {
          if (_?.reminder && __?.reminder) {
            const dateA = new Date(_?.reminder).getTime();
            const dateB = new Date(__?.reminder).getTime();
            return dateA - dateB;
          } else {
            return 0;
          }
        },
        sortable: true,
        reorder: true,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
        data: { column, enabled: true },
      };

    case "deadline":
      return {
        title: t("common.deadline"),
        key: "date",
        render: (t: BaseTask) => {
          const deadline = (t as OrphanTask).deadline;

          return {
            children: deadline ? (
              <DeadlineCell $status={getDeadlineCellStatus(deadline)}>
                <AtiraIcon
                  icon={
                    isBefore(deadline, new Date()) && !isToday(deadline)
                      ? faTriangleExclamation
                      : faCalendarCheck
                  }
                  size="lg"
                />
                <Flex alignItems="center" gap="xs">
                  <DeadlineStatusText>
                    {getDeadlineStatusLabel(deadline, functions.t)}
                  </DeadlineStatusText>
                  <DeadlineDateText>
                    {standardDate<string>(new Date(deadline), user, true)}
                  </DeadlineDateText>
                </Flex>
              </DeadlineCell>
            ) : null,
          };
        },
        sorter: (_: OrphanTask, __: OrphanTask) => {
          if (_?.deadline && __?.deadline) {
            const dateA = new Date(_?.deadline).getTime();
            const dateB = new Date(__?.deadline).getTime();
            return dateA - dateB;
          } else {
            return 0;
          }
        },
        sortable: true,
        reorder: true,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
        data: { column, enabled: true },
      };

    case "contactAttempts":
      return {
        title: t("common.contactAttempts"),
        key: "contactAttempts",
        render: (_: BaseTask) =>
          isNumber((_ as any)?.contactAttempts) ? (
            <TextWithTruncated length={30}>
              {(_ as any)?.contactAttempts.toString()}
            </TextWithTruncated>
          ) : null,
        sortable: true,
        reorder: true,
        data: { column, enabled: true },
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };

    case "priority":
      return {
        title: t("common.priority"),
        key: "priority",
        render: (task: BaseTask) => {
          const priority = (task as ContactTask | OrphanTask)?.priority;

          if (
            !priority ||
            (task?.kind?.toLowerCase() !== TaskKind.CONTACT.toLowerCase() &&
              task?.kind?.toLowerCase() !== TaskKind.ORPHAN.toLowerCase())
          ) {
            return null;
          }

          const status =
            priority.toLowerCase() === "high"
              ? "danger"
              : priority.toLowerCase() === "medium"
                ? "warning"
                : "success";

          return (
            <PriorityCell $status={status}>
              <AtiraIcon icon={faFlag} size="sm" />
              <Text fontSize="s">{t(`common.${priority.toLowerCase()}`)}</Text>
            </PriorityCell>
          );
        },
        data: { column, enabled: true },
      };

    case "done":
      return {
        title: t("common.done"),
        key: "done",
        render: (task: BaseTask) => (
          <Flex justifyContent="center" alignItems="center" gap="s">
            <AtiraIcon
              size="lg"
              icon={task.done ? faCheck : faXmark}
              color={task.done ? "success" : "danger"}
            />
            {task.doneByUser && task.doneAt ? (
              <AssigneeBanner
                title={t("common.closedBy", {
                  name: task.doneByUser?.name,
                  date: standardDate<string>(
                    new Date(task.doneAt!),
                    user,
                    true,
                  ),
                })}
                hideSelf
                assignees={[task.doneByUser]}
              />
            ) : null}
          </Flex>
        ),
        data: { column, enabled: true },
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
        fixed: "left",
      };

    case "relatedTo":
      return {
        title: t("common.relatedTo"),
        key: "relatedTo",
        render: (task: BaseTask) =>
          task.parent ? (
            <RelatedToButton onClick={() => functions.previewRelatedTo(task)}>
              <Text cutText>
                {(task.parent as Entry)?.fields?.name ||
                  (task.parent as Entry)?.fields?.email ||
                  (task.parent as KanbanCard)?.title}
              </Text>

              <AtiraIcon
                icon={
                  i18n.language === AppLangs.AR ? faAngleLeft : faAngleRight
                }
              />
            </RelatedToButton>
          ) : null,
        sortable: true,
        reorder: true,
        data: { column, enabled: true },
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };

    default:
      return {
        title: t(`common.${column}`),
        key: column,
        render: (task: BaseTask) => (
          <TextWithTruncated length={30}>
            {(task[column] as string) || ""}
          </TextWithTruncated>
        ),
        sortable: true,
        reorder: true,
        data: { column, enabled: true },
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };
  }
};

export const createTasksColumns = (
  kind: TaskKind,
  functions: FieldFunctions,
  permissions: Permission["permissions"],
  user: User,
  members?: User[],
) => {
  const keys = [
    "actions",
    "assigneeIds",
    "name",
    "priority",
    "deadline",
    "done",
  ] as TaskKeys[];

  return keys.map((t) =>
    getFieldInfo(t, functions, permissions, user, members),
  );
};
